import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Cell, Row } from '@skupreme/shared/ui';
import { useDebouncedEffect } from '@hooks/debounceAsync';
import { DateFormat, DateUtility } from '@utility/date';
import type { ITableColumn } from '../../legacy-bridge';
import { ReadOnlyTip, useOrdersV2 } from '../../hooks/orders-v2';
import { useHeldOrders } from '../../hooks/use-held-orders';
import { useV2StatusCounts } from '../../hooks/use-v2-status-counts';
import { formatHeldFor } from '../../lib/age';
import { type HeldOrderRow, holdReasonLabel } from '../../lib/hold-reason';
import { buildOrderListParams } from '../../lib/list-params';
import {
  getOrderHref,
  isPlainClick,
  type OrderNavigationState,
} from '../../lib/order-navigation';
import { showsStageOn } from '../../lib/stage';
import { HELD_STATUS } from '../../lib/status-tabs';
import { OrderAgeCell } from '../cells/OrderAgeCell';
import { OrderIdCell } from '../cells/OrderIdCell';
import { OrderSearch } from '../header/OrderSearch';
import { OrderStatusTabs } from '../header/OrderStatusTabs';
import { ColumnHeader } from '../table/ColumnHeader';
import { OrdersTable } from '../table/OrdersTable';

export const NOTHING_HELD = 'Nothing is waiting for an operator.';
export const NO_HELD_MATCH = 'No Orders Found';

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 500;

/* Widths are shares of the 16-unit grid the orders table converts. */
const COLUMNS = (
  [
    { key: 'order_id', text: 'ID', width: 5 },
    { key: 'channel', text: 'Channel', width: 2 },
    { key: 'placed', text: 'Placed', width: 2 },
    { key: 'items', text: 'Items', width: 1 },
    { key: 'reason', text: 'Hold reason', width: 4 },
    { key: 'held_for', text: 'Held for', width: 2 },
  ] satisfies (ITableColumn & { text: string })[]
).map(
  (column): ITableColumn => ({
    ...column,
    textAlign: 'left',
    component: <ColumnHeader label={column.text} />,
  }),
);

function SecondLine({ text }: { text?: string }) {
  return text ? (
    <span className="sk:block sk:text-xs sk:text-fg-tertiary">{text}</span>
  ) : null;
}

function HeldOrderTableRow({
  row,
  navigation,
}: {
  row: HeldOrderRow;
  navigation: OrderNavigationState;
}) {
  const navigate = useNavigate();
  const href = getOrderHref(row._id);
  const channel = row.marketplace?.type || row.marketplace?.name;

  return (
    <Row
      id={row._id}
      href={href}
      onClick={event => {
        if (!isPlainClick(event)) {
          return;
        }
        event.preventDefault();
        navigate(href, { state: navigation });
      }}
      className="sk:cursor-pointer sk:align-middle"
    >
      <Cell>
        {/* The hold itself is the Hold reason column; the row does not repeat it as an issue. */}
        <OrderIdCell
          order={{
            ...row,
            order_error: row.order_error?.filter(
              error =>
                !(
                  error.process === row.hold?.kind &&
                  error.reason === row.hold?.reason_code
                ),
            ),
          }}
          showStatusReason={false}
          showStage={showsStageOn(HELD_STATUS, row)}
        />
      </Cell>
      <Cell>
        {channel || '—'}
        <SecondLine
          text={row.marketplace?.name !== channel ? row.marketplace?.name : ''}
        />
      </Cell>
      <Cell>
        <OrderAgeCell acknowledgedAt={row.time_acknowledged} />
      </Cell>
      <Cell>{row.total_items}</Cell>
      <Cell>{row.status_reason?.message || holdReasonLabel(row.hold)}</Cell>
      <Cell>
        {row.hold ? formatHeldFor(row.hold.age_seconds) : '—'}
        <SecondLine
          text={
            row.hold
              ? DateUtility.formatDate(
                  row.hold.held_since,
                  DateFormat.meridianTimeDate,
                )
              : ''
          }
        />
      </Cell>
    </Row>
  );
}

/**
 * EN-4204: the held tab of the V2 orders page. Orders an operator has to act
 * on, with what holds each one and for how long. Only search narrows it; the
 * order is the API's.
 */
export function HeldOrdersList() {
  const { isV2 } = useOrdersV2();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  /* Search and page move together: a new search reads from its first page. */
  const [view, setView] = useState({ search: '', page: 1, limit: PAGE_SIZE });

  useDebouncedEffect(
    () => {
      const search = query.trim();
      setView(current =>
        current.search === search ? current : { ...current, search, page: 1 },
      );
    },
    [query],
    SEARCH_DELAY_MS,
  );

  const params = useMemo(
    () =>
      buildOrderListParams(
        { page: view.page, limit: view.limit },
        HELD_STATUS,
        view.search,
      ),
    [view],
  );

  const held = useHeldOrders(params, isV2);
  const counts = useV2StatusCounts(params, isV2);

  /* Carried into the details page, so closing an order comes back to this list. */
  const navigation = useMemo<OrderNavigationState>(
    () => ({
      orderIds: held.rows.map(row => row._id),
      listUrl: `${location.pathname}${location.search}`,
    }),
    [held.rows, location.pathname, location.search],
  );

  const emptyMessage =
    held.error || (view.search ? NO_HELD_MATCH : NOTHING_HELD);

  return (
    <>
      <div className="sk:mx-3 sk:mb-1 sk:flex sk:h-12 sk:shrink-0 sk:items-center sk:gap-2">
        <OrderStatusTabs counts={counts.rows} />
        <OrderSearch
          className="sk:ml-auto"
          onQueryChange={setQuery}
          onSelect={order => navigate(getOrderHref(order._id))}
        />
      </div>
      <div className="sk:px-4">
        {held.notSupported.length > 0 ? (
          <ReadOnlyTip>
            <span className="sk:text-xs sk:text-fg-tertiary">
              Not applied on this list: {held.notSupported.join(', ')}
            </span>
          </ReadOnlyTip>
        ) : null}
        <OrdersTable<HeldOrderRow>
          columns={COLUMNS}
          data={held.rows}
          loading={held.loading}
          loadingRows={5}
          noDataMessage={emptyMessage}
          currentPage={view.page}
          pageSize={view.limit}
          totalData={held.totalData}
          pageChanged={page => setView(current => ({ ...current, page }))}
          pageSizeChanged={limit =>
            setView(current => ({ ...current, limit, page: 1 }))
          }
          renderRow={row => (
            <HeldOrderTableRow
              key={row._id}
              row={row}
              navigation={navigation}
            />
          )}
        />
      </div>
    </>
  );
}
